import { Box, CircularProgress, Divider, Typography } from '@mui/material';
import { useCallback } from 'react';

import { RelatedCountFields } from '~/components/common/RelatedCountFields';
import { ElevatorComplianceFields } from '~/components/compliance/detailPage/ElevatorComplianceFields';
import { ElevatorDetailPageHeader } from '~/components/compliance/detailPage/ElevatorDetailPageHeader';
import { ElevatorTabContainer } from '~/components/compliance/detailPage/ElevatorTabContainer';
import { useCarRelatedCounts } from '~/components/compliance/detailPage/useCarRelatedCounts';
import { useKnowledgeEnabled } from '~/components/knowledge/useKnowledgeEnabled';
import { useNeedsAttentionCount } from '~/components/workQueue/useNeedsAttentionItems';
import { useActionItemsRefresh } from '~/contexts/actionItemsRefreshContext';
import type { Car } from '~/data/generated/model';
import { useCarKnowledge } from '~/data/hooks/useKnowledge';
import type { API_Compliance } from '~/data/schemas/compliance';
import { DetailTableHeightProvider } from '~/hooks/useDetailTableHeight';
import { pageBandColor, pageBandDividerColor } from '~/theme';

// The preview has no tab strip, so the counts its tabs would carry are listed instead.
function ElevatorRelatedCounts({ compliance }: { compliance: API_Compliance }) {
  const counts = useCarRelatedCounts({
    carId: compliance.carId,
    propertyId: compliance.propertyId,
  });
  const needsAttention = useNeedsAttentionCount(compliance.propertyId, compliance.carId);
  const knowledgeEnabled = useKnowledgeEnabled();
  const knowledge = useCarKnowledge(compliance.carId);
  const knowledgeCount =
    knowledge.isLoading || knowledge.error
      ? undefined
      : knowledge.entries.filter((e) => !e.supersededById).length;
  return (
    <RelatedCountFields
      counts={[
        { label: 'Needs Attention', value: needsAttention },
        ...(knowledgeEnabled ? [{ label: 'Knowledge', value: knowledgeCount }] : []),
        { label: 'Tickets', value: counts.tickets },
        { label: 'Invoices', value: counts.invoices },
        { label: 'Proposals', value: counts.proposals },
      ]}
    />
  );
}

interface ElevatorDetailPageProps {
  compliance: API_Compliance | null;
  car: Car | undefined;
  carLoading: boolean;
  isLoading: boolean;
  /** Re-reads the car after a certificate upload or an edit. */
  refresh: () => void;
  /** `null` in a dialog, which closes from its own title bar. */
  onBack: (() => void) | null;
  backLabel?: string;
  variant?: 'page' | 'preview';
}

/**
 * Full-page elevator detail: a header band naming the car, over three tabs — Compliance
 * for its certificate and violations, Service & Performance for the property grid's
 * columns, Asset History for the timeline.
 *
 * Nothing appears in both the band and a tab: the band is which elevator this is, the
 * tabs are what is known about it.
 */
export function ElevatorDetailPage({
  compliance,
  car,
  carLoading,
  isLoading,
  refresh,
  onBack,
  backLabel,
  variant = 'page',
}: ElevatorDetailPageProps) {
  const isPreview = variant === 'preview';
  const { refresh: refreshActionItems } = useActionItemsRefresh();

  // Adding a certificate can clear a compliance Backlog item, so the Backlog list and
  // badge are refreshed alongside the record itself.
  const onRecordChanged = useCallback(() => {
    refresh();
    refreshActionItems();
  }, [refresh, refreshActionItems]);

  return (
    <DetailTableHeightProvider>
      <Box
        display="flex"
        flexDirection="column"
        minHeight="100%"
        bgcolor={isPreview ? pageBandColor : 'grey.50'}
      >
        <ElevatorDetailPageHeader
          compliance={compliance}
          car={car}
          carLoading={carLoading}
          onBack={onBack}
          backLabel={backLabel}
          onCarEdited={onRecordChanged}
          compact={isPreview}
          footer={
            isPreview && compliance ? (
              <>
                <Divider sx={{ my: 2, borderColor: pageBandDividerColor }} />
                <Typography variant="overline" color="text.secondary" display="block" mb={1}>
                  Certificate
                </Typography>
                <ElevatorComplianceFields compliance={compliance} showDownload />
                <ElevatorRelatedCounts compliance={compliance} />
              </>
            ) : null
          }
        />

        {/* The tabs need the compliance record, not a skeleton of one. The header renders its
            own loading state above, so the page keeps its shape while this spins. */}
        <Box display={isPreview ? 'none' : 'flex'} flex={1} minHeight={0} minWidth={0}>
          {isPreview ? null : compliance ? (
            <ElevatorTabContainer compliance={compliance} onCertificateAdded={onRecordChanged} />
          ) : isLoading ? (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              flex={1}
              role="status"
              aria-label="Loading elevator"
            >
              <CircularProgress />
            </Box>
          ) : null}
        </Box>
      </Box>
    </DetailTableHeightProvider>
  );
}
